/**
 * useStoryCollaborators — rows for the story header's collaborators dropdown.
 *
 * Reads the same query as the Share modal (`useStoryInvitees`), so an invite
 * sent from Collaborate refreshes this list through `useCreateStoryInvitees`'
 * existing invalidation — no second cache to keep in sync.
 */
import { useMemo } from "react";
import { getApiErrorMessage } from "../services/apiErrors";
import { mapStoryInviteesToCollaboratorRows } from "../services/storyInvitees/storyInviteesMappers";
import type { StoryCollaboratorRow } from "../services/dataStories/storyMembers.types";
import { useStoryInvitees } from "./useStoryInvitees";
import { useOwnAvatarFallback } from "./useOwnAvatarFallback";

export interface StoryCollaborators {
  collaborators: StoryCollaboratorRow[];
  isLoading: boolean;
  /** Backend's reason when the list fails (e.g. non-owners get a 404); null otherwise. */
  errorMessage: string | null;
}

export function useStoryCollaborators(storyId: string): StoryCollaborators {
  const { data, isLoading, error } = useStoryInvitees(storyId);
  const withOwnAvatar = useOwnAvatarFallback();

  const collaborators = useMemo(
    () => mapStoryInviteesToCollaboratorRows(data ?? []).map(withOwnAvatar),
    [data, withOwnAvatar],
  );

  return {
    collaborators,
    isLoading,
    errorMessage: error
      ? getApiErrorMessage(error, "Unable to load collaborators.")
      : null,
  };
}
